/**
 * Display formatting for exercise names.
 *
 * The bundled catalogue stores every one of its 1,500 names in lower case
 * (`"band underhand pulldown with classic"`). That is the dataset's own form and
 * is **never** rewritten in the database — the stored value has to keep matching
 * the recorded provenance, and `nameSearch` is derived from it. Capitalisation
 * is therefore a rendering concern, and lives here.
 *
 * CSS `text-transform: capitalize` was rejected: it capitalises every word,
 * producing "Pulldown With Classic" and "Cable Cross-over Variation". Real title
 * case needs to know which words stay lower, which is a data problem, not a
 * styling one.
 *
 * Pure and dependency-free, so it is trivially testable and can be reused by any
 * later phase that displays an exercise name.
 */

/**
 * Words that stay lower case inside a title — articles, coordinating
 * conjunctions and short prepositions. Never applied to the first or last word.
 */
const MINOR_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in', 'into',
  'nor', 'of', 'off', 'on', 'onto', 'or', 'over', 'per', 'the', 'to', 'up',
  'via', 'with',
]);

/**
 * Tokens with a fixed casing the generic rules would get wrong.
 *
 * Keyed by the lower-cased token. Deliberately small: this is for genuine
 * initialisms and unit-like words that appear in the catalogue, not a general
 * dictionary. An unknown word simply gets ordinary title casing.
 */
const FIXED_CASE = new Map<string, string>([
  ['ez', 'EZ'],
  ['iso', 'ISO'],
  ['bosu', 'BOSU'],
  ['v', 'V'],
  ['t', 'T'],
  ['ii', 'II'],
  ['iii', 'III'],
]);

/** Upper-case the first letter, leave the rest as given. */
function capitaliseFirst(word: string): string {
  if (word.length === 0) return word;
  return word[0]!.toUpperCase() + word.slice(1);
}

/**
 * Capitalise one whitespace-delimited word, handling internal punctuation.
 *
 * Hyphenated and slashed compounds are capitalised on both sides — "cross-over"
 * becomes "Cross-Over", "push/pull" becomes "Push/Pull" — because each part
 * reads as a word in its own right. Apostrophes are NOT treated this way:
 * "farmer's" must not become "Farmer'S".
 */
function capitaliseWord(word: string): string {
  return word
    .split(/([-/])/)
    .map((part) => {
      if (part === '-' || part === '/') return part;
      const fixed = FIXED_CASE.get(part.toLowerCase());
      if (fixed !== undefined) return fixed;
      return capitaliseFirst(part);
    })
    .join('');
}

/**
 * Format a stored exercise name for display.
 *
 * A name that already contains capitals — which every user-created exercise
 * will, since the user typed it — is returned untouched. Re-casing someone's
 * own text would be presumptuous and would fight them if they meant it.
 */
export function formatExerciseName(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return '';

  // Only normalise names that arrived entirely lower case, i.e. the catalogue's.
  if (trimmed !== trimmed.toLowerCase()) return trimmed;

  const words = trimmed.split(/\s+/);

  return words
    .map((word, index) => {
      const isFirst = index === 0;
      const isLast = index === words.length - 1;

      if (!isFirst && !isLast && MINOR_WORDS.has(word.toLowerCase())) {
        return word.toLowerCase();
      }
      return capitaliseWord(word);
    })
    .join(' ');
}
